import { forwardRef, useImperativeHandle, useMemo, useRef } from "react";
import "./VariableHighlightEditor.css";

export interface VariableHighlightEditorHandle {
	/** Inserts {{variableName}} at the current caret position. */
	insertVariable: (variableName: string) => void;
	focus: () => void;
}

interface VariableHighlightEditorProps {
	value: string;
	onChange: (value: string) => void;
	/** Variable names considered valid; everything else is flagged as unknown. */
	knownVariables?: string[];
	disabled?: boolean;
	placeholder?: string;
	minHeight?: number;
	id?: string;
	ariaLabel?: string;
}

// Matches {{ variable }} placeholders (no nested braces).
const VARIABLE_REGEX = /\{\{([^{}]*?)\}\}/g;

const escapeHtml = (value: string): string =>
	value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/**
 * A plain-text template editor that visually highlights {{variables}} without a
 * heavyweight editor dependency. It layers a styled, read-only "backdrop" behind
 * a transparent <textarea>: the user types in the textarea (real caret, real
 * selection) while the backdrop renders the same text with each placeholder
 * wrapped in a colored token. Known variables are green, unknown ones amber.
 */
const VariableHighlightEditor = forwardRef<
	VariableHighlightEditorHandle,
	VariableHighlightEditorProps
>((props, ref) => {
	const textareaRef = useRef<HTMLTextAreaElement>(null);
	const backdropRef = useRef<HTMLDivElement>(null);

	const known = useMemo(
		() => new Set((props.knownVariables ?? []).map((variable) => variable.trim())),
		[props.knownVariables]
	);

	const highlightedHtml = useMemo(() => {
		const escaped = escapeHtml(props.value ?? "");
		const withTokens = escaped.replace(VARIABLE_REGEX, (match: string, rawName: string) => {
			const name = String(rawName).trim();
			const modifier = known.has(name) ? "vhe-token--known" : "vhe-token--unknown";
			return `<span class="vhe-token ${modifier}">${match}</span>`;
		});
		// Trailing newline keeps the backdrop's last line aligned with the textarea.
		return { __html: `${withTokens}\n` };
	}, [props.value, known]);

	const syncScroll = () => {
		if (textareaRef.current && backdropRef.current) {
			backdropRef.current.scrollTop = textareaRef.current.scrollTop;
			backdropRef.current.scrollLeft = textareaRef.current.scrollLeft;
		}
	};

	useImperativeHandle(ref, () => ({
		insertVariable: (variableName: string) => {
			const token = `{{${variableName}}}`;
			const element = textareaRef.current;
			const current = props.value ?? "";
			if (!element) {
				props.onChange(current + token);
				return;
			}
			const start = element.selectionStart ?? current.length;
			const end = element.selectionEnd ?? current.length;
			props.onChange(current.slice(0, start) + token + current.slice(end));
			// Restore the caret right after the inserted token on the next frame,
			// once React has applied the new value.
			requestAnimationFrame(() => {
				element.focus();
				const caret = start + token.length;
				element.setSelectionRange(caret, caret);
				syncScroll();
			});
		},
		focus: () => textareaRef.current?.focus(),
	}));

	const minHeight = props.minHeight ?? 260;

	return (
		<div className="vhe-container">
			<div
				className="vhe-backdrop"
				ref={backdropRef}
				aria-hidden="true"
			>
				<div
					className="vhe-highlights"
					dangerouslySetInnerHTML={highlightedHtml}
				/>
			</div>
			<textarea
				id={props.id}
				ref={textareaRef}
				className="vhe-textarea"
				style={{ minHeight }}
				value={props.value ?? ""}
				disabled={props.disabled}
				placeholder={props.placeholder}
				aria-label={props.ariaLabel}
				spellCheck={false}
				onChange={(event) => props.onChange(event.target.value)}
				onScroll={syncScroll}
			/>
		</div>
	);
});

VariableHighlightEditor.displayName = "VariableHighlightEditor";

export default VariableHighlightEditor;
